import { generateColorScheme as generateColorSchemeHelper } from './schemeGenerator';
import type { AccessibleTextScheme, ColorScheme } from './ColorScheme';
import { createTheme, IPalette, ITheme } from '@fluentui/style-utilities';
import Color from 'color';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import loc from 'owa-localize';
import { getOpxHostApi } from 'owa-opx';
import { DEFAULT_OUTLOOK_THEME_COLOR, getPalette, getPaletteAsRawColors } from 'owa-theme';
import {
    blueCalendarColorText,
    greenCalendarColorText,
    redCalendarColorText,
    navyCalendarColorText,
    grayCalendarColorText,
    tealCalendarColorText,
    magentaCalendarColorText,
    orangeCalendarColorText,
    purpleCalendarColorText,
} from './colorUtils.locstring.json';
import { computed } from 'mobx';

const MINIMUM_TEXT_CONTRAST = 4.5;
const TEXT_CONTRAST_WHITE_PREFERENCE_BUFFER = 0.3;

/**
 * Returns the default color based on the theme
 */
export function getDefaultColor(): string {
    if (
        isHostAppFeatureEnabled('defaultCalendarColor') &&
        getOpxHostApi().getDefaultCalendarColor
    ) {
        return getOpxHostApi().getDefaultCalendarColor();
    }

    const rawPaletteColor = DEFAULT_OUTLOOK_THEME_COLOR;
    return !rawPaletteColor ||
        rawPaletteColor.toUpperCase() === DEPRECATED_PRIMARY_BLUE_CALENDAR_COLOR
        ? PRIMARY_BLUE_CALENDAR_COLOR
        : rawPaletteColor.toUpperCase();
}

/**
 * Use for shim purposes only
 *
 * Deprecated in order to satisfy strict check for minimum A11Y contrast ratio
 * when white text/icons are placed on this background color
 */
export const DEPRECATED_ORANGE_CALENDAR_COLOR = '#DC3B00';
export const ORANGE_CALENDAR_COLOR = '#DA3B01';

/**
 * Due to cobranding, we occasionally still load a theme with the deprecated theme color that does not pass accessibility. Replace with new color
 */
export const DEPRECATED_PRIMARY_BLUE_CALENDAR_COLOR = '#0078D7';
export const PRIMARY_BLUE_CALENDAR_COLOR = DEFAULT_OUTLOOK_THEME_COLOR;

/**
 * Returns the light calendar color scheme, for scenarios where calendar items should be deprioritized visually
 */
export function getLightColorScheme() {
    return generateColorScheme(getPaletteAsRawColors().neutralLight);
}

/**
 * Return whether the color scheme's primary color is the default color or not
 */
export function isDefaultColor(colorScheme: ColorScheme): boolean {
    return colorScheme.calPrimary.toUpperCase() === getDefaultColor().toUpperCase();
}

/**
 * These are the default 9 calPrimary colors for users to pick from without picking their own custom hex values
 */
function getDefaultPrimaryColors(): { [key: string]: () => string } {
    return {
        [getDefaultColor()]: () => loc(blueCalendarColorText), // blue
        '#498205': () => loc(greenCalendarColorText), // green
        '#C50F1F': () => loc(redCalendarColorText), // Red
        '#003966': () => loc(navyCalendarColorText), // Navy
        '#69797E': () => loc(grayCalendarColorText), // Gray
        '#038387': () => loc(tealCalendarColorText), // Teal
        '#E3008C': () => loc(magentaCalendarColorText), // Magenta
        [ORANGE_CALENDAR_COLOR]: () => loc(orangeCalendarColorText), // Orange
        '#744DA9': () => loc(purpleCalendarColorText), // Purple
    };
}

/**
 * Adds the given opacity to the given color value and returns a string representation of the result
 */
export function addOpacityToColor(colorString: string, opacity: number): string {
    let color = Color(colorString);
    return color.alpha(opacity).toString();
}

/**
 * Converts the given color string to HSB (HSV), then adds the brightness to it. Brightness will be bounded
 * between [0, 100]
 */
export function addBrightnessToColor(colorString: string, brightness: number): string {
    const color = Color(colorString);
    const existingB = color.value();
    const newB = Math.max(Math.min(existingB + brightness, 100), 0);
    return color.value(newB).toString();
}

/**
 * Converts the given color string to HSB (HSV), then adds the saturation to it. Saturation will be bounded
 * between [0, 100]
 */
export function addSaturationToColor(colorString: string, saturation: number): string {
    const color = Color(colorString);
    const existingS = color.saturationv();
    const newS = Math.max(Math.min(existingS + saturation, 100), 0);
    return color.saturationv(newS).toString();
}

/**
 * We dynamically generate a color scheme given a primary color value by taking the primary color and shading it with a % of black and % of white
 *
 * The percentages of white and black come from shadeValues, and create a ColorScheme where the given primary color corresponds to calPrimary, with each darker
 * shade mixing extra black and each lighter shade mixing extra white.
 *
 * Code from universal color generation is here:
 * https://msasg.visualstudio.com/_search?type=code&lp=custom-Collection&text=CalendarColorShadeGenerator&result=DefaultCollection%2FOfficeSourceDepot%2Fdevmain%2Fdevmain%2Fhxcalendarui%2Fxaml%2FSupport%2FcolorShadeGenerator%2FCalendarColorShadeGenerator.cpp&preview=1&_a=search
 *
 * @param primaryHexColor the string value of the color to use as primary
 */
export function generateColorScheme(primaryHexColor: string): ColorScheme {
    const colorScheme = generateColorSchemeHelper(primaryHexColor, getIsDarkTheme());
    colorScheme.accessibleText = calculateAccessibleText(colorScheme);
    colorScheme.text = colorScheme.accessibleText.calPrimaryBackground;
    colorScheme.textAlt = colorScheme.accessibleText.calLighterBackground;

    return colorScheme;
}

/**
 * Computes the result of overlapping multiple colors on top of each other. This only has meaning if the colors
 * overlapping have opacity to them- if the top color has alpha = 1.0, the result will just be the top color.
 *
 * this is called alpha compositing: https://en.wikipedia.org/wiki/Alpha_compositing, an implementation of the "over" operation
 * @param colors the colors to overlap. Descending order (first color is on top, second color is the second from the top, etc.)
 * @returns string representation of the resulting color.
 */
export function computeResultingOverlapColor(...colors: string[]): string {
    let total = Color(colors[colors.length - 1]);

    for (let i = colors.length - 2; i >= 0; i--) {
        let top = Color(colors[i]);
        let totalAlpha = top.alpha() + total.alpha() * (1 - top.alpha());
        total = Color({
            r: Math.floor(
                (top.alpha() * top.red() + total.alpha() * (1 - top.alpha()) * total.red()) /
                    totalAlpha
            ),
            g: Math.floor(
                (top.alpha() * top.green() + total.alpha() * (1 - top.alpha()) * total.green()) /
                    totalAlpha
            ),
            b: Math.floor(
                (top.alpha() * top.blue() + total.alpha() * (1 - top.alpha()) * total.blue()) /
                    totalAlpha
            ),
            alpha: totalAlpha,
        });
    }

    return total.toString();
}

/**
 * Calculates optimal text color from the colorScheme for the given background color
 *
 * given an the background color and an array of possible colors to use for text on it, we find the closest of the given colors + white/black
 * to the minimum contrast that still exceeds the minimum contrast. If the best color is black, returns a color with the same Hue and Saturation as
 * calDarker, and a brightness Value set to as bright as possible while still passing minimum contrast
 *
 * @param backgroundColor the background color for the text to go on
 * @param colorArray the array of colors to search for a possible value. Will also try black and white
 * @param desiredColorString the color to use assuming it passes the contrast check
 * @param minimumContrast the minimum contrast the text must pass
 */
export function getOptimalTextColorString(
    backgroundColor: string,
    colorScheme: ColorScheme,
    desiredColorString: string,
    minimumContrast: number = MINIMUM_TEXT_CONTRAST
): string {
    // first see if desired color passes, if so we can just return that immediately
    let background = Color(backgroundColor);
    let desiredColor = Color(desiredColorString);
    if (getContrastRatio(desiredColor, background) >= minimumContrast) {
        return desiredColor.hex();
    }

    // if desired color doesn't pass, look through all the colors in the colorScheme, and find the one closest to
    // the minimum contrast that still passes, if any
    let closestContrast = 21; // contrast between black and white, largest value for a contrast
    let bestColor: Color;
    // also keep track of the contrast and color that come closest to satisfying the minimum contrast, in case we are never able to find a value that passes
    let fallbackContrast = 0;
    let fallbackColor: Color;
    Object.keys(colorScheme).forEach(colorKey => {
        const colorString = colorScheme[colorKey];
        if (colorString && typeof colorString == 'string') {
            let color = Color(colorString);
            let contrast = getContrastRatio(color, background);
            if (contrast >= minimumContrast && contrast < closestContrast) {
                bestColor = color;
                closestContrast = contrast;
                // keep track of most optimal fallback color/contrast
            } else if (contrast < minimumContrast && contrast > fallbackContrast) {
                fallbackColor = color;
                fallbackContrast = contrast;
            }
        }
    });

    if (bestColor) {
        return bestColor.hex();
    }

    // if we still haven't found a color good enough, we need to check black and white (adding both black and white guarantees we'll pass)
    // check white first over black
    let whiteColor = Color({ r: 255, g: 255, b: 255 });
    let whiteContrast = whiteColor.contrast(background);
    if (whiteContrast >= minimumContrast) {
        return whiteColor.hex();
        // continue tracking most optimal fallback color/contrast
    } else if (whiteContrast < minimumContrast && whiteContrast > fallbackContrast) {
        fallbackColor = whiteColor;
        fallbackContrast = whiteContrast;
    }

    // finally, we're forced to use black. But we'd ideally like at least a shade of the color that's the same as primary, so rather than just use black
    // we'll search for a value that's dark enough to pass contrast, but still the same hue and saturation as the primary value
    // so we'll start from calPrimary and binary search between calDarker and black, adjusting the V value of the HSV,
    // to find the lightest color of the same H and S value that still passes minimumContrast
    let currentColor = Color(colorScheme.calPrimary);

    let lowerVBound = 0;
    let upperVBound = currentColor.value();

    // we take 7 steps because binary searching for a V value between 0 and 100, 2^7 = 128, so we're sure we'll hit a good value
    for (let steps = 0; steps < 7; steps++) {
        let currentV = (upperVBound + lowerVBound) / 2;

        currentColor = currentColor.value(currentV);
        let newContrast = currentColor.contrast(background);

        // continue tracking most optimal fallback color/contrast
        if (newContrast < minimumContrast && newContrast > fallbackContrast) {
            fallbackColor = currentColor;
            fallbackContrast = newContrast;
        }

        if (newContrast < minimumContrast + 0.1) {
            upperVBound = currentV;
        } else {
            lowerVBound = currentV;
        }
    }

    // choose the color that passes the minimum contrast, or else comes closest to satisfying the minimum contrast
    let currentContrast = currentColor.contrast(background);
    if (currentContrast < minimumContrast && fallbackColor && currentContrast < fallbackContrast) {
        currentColor = fallbackColor;
        currentContrast = fallbackContrast;
    }

    if (
        whiteContrast >= MINIMUM_TEXT_CONTRAST &&
        Math.abs(whiteContrast - currentContrast) < TEXT_CONTRAST_WHITE_PREFERENCE_BUFFER
    ) {
        // in some scenarios, the requested minimum contrast is > default minimum.
        // here, white and black might both have failed, and black might have higher contrast with the background color than white.
        // howver, per request from design, even if color contrast is technically better with black, prefer white if it at least passes
        // the default minimum in those cases, and the difference is smaller than some delta
        return whiteColor.hex();
    }

    return currentColor.hex();
}

/**
 * Generates the default color schemes and returns them as an array
 */
export function generateDefaultColorSchemes(): ColorScheme[] {
    return Object.keys(getDefaultPrimaryColors()).map(color => generateColorScheme(color));
}

/**
 * Create a Fluent Theme from color scheme
 * @param scheme ColorScheme object
 * @return the theme based on this color scheme
 */
export function themeFromColorScheme(scheme: ColorScheme): ITheme {
    return createTheme({
        palette: {
            ...(getPalette() as IPalette),
            themePrimary: scheme.calPrimary,
            themeLighterAlt: scheme.calLighterAlt,
            themeLighter: scheme.calLighter,
            themeLight: scheme.calLight,
            themeTertiary: scheme.calTertiary,
            themeSecondary: scheme.calSecondary,
            themeDarkAlt: scheme.calDarkAlt,
            themeDark: scheme.calDark,
            themeDarker: scheme.calDarker,
        },
        semanticColors: {
            menuHeader: scheme.accessibleText.msColorWhiteBackground,
        },
    });
}

const themeColorSchemeGetter = computed(() => {
    const palette = getPaletteAsRawColors();
    let colorScheme: ColorScheme = {
        accessibleText: {
            calPrimaryBackground: '',
            calLighterBackground: '',
            msColorWhiteBackground: '',
            msColorNeutralLighterBackground: '',
        },
        calDarker: palette.themeDarker,
        calDark: palette.themeDark,
        calDarkAlt: palette.themeDarkAlt,
        calPrimary: palette.themePrimary,
        calSecondary: palette.themeSecondary,
        calTertiary: palette.themeTertiary,
        calLight: palette.themeLight,
        calLighter: palette.themeLighter,
        calLighterAlt: palette.themeLighterAlt,
    };

    colorScheme.accessibleText = calculateAccessibleText(colorScheme);

    return colorScheme;
});

/**
 * Create a calendar color scheme from current theme
 * @return a calendar color scheme based on the theme
 */
export function getThemeColorScheme(): ColorScheme {
    return themeColorSchemeGetter.get();
}

/**
 * Calculates accessible passing text colors for various background colors. Uses the color scheme as a base and adjusts the color as necessary to get
 * minimum contrast for the given backgrounds.
 * @param colorScheme the color scheme
 */
function calculateAccessibleText(colorScheme: ColorScheme): AccessibleTextScheme {
    let whiteColor = getPaletteAsRawColors().white;

    return {
        calPrimaryBackground: getOptimalTextColorString(
            colorScheme.calPrimary,
            colorScheme,
            whiteColor,
            MINIMUM_TEXT_CONTRAST
        ),
        calLighterBackground: getOptimalTextColorString(
            colorScheme.calLighter,
            colorScheme,
            whiteColor,
            MINIMUM_TEXT_CONTRAST
        ),
        msColorWhiteBackground: getOptimalTextColorString(
            getPaletteAsRawColors().white,
            colorScheme,
            whiteColor,
            MINIMUM_TEXT_CONTRAST
        ),
        msColorNeutralLighterBackground: getOptimalTextColorString(
            getPaletteAsRawColors().neutralLighter,
            colorScheme,
            whiteColor,
            MINIMUM_TEXT_CONTRAST
        ),
    };
}

export function getColorName(color: string): string {
    const defaultColors = getDefaultPrimaryColors();
    return defaultColors[color] ? defaultColors[color]() : color;
}

function getContrastRatio(color: Color, backgroundColor: Color): number {
    return Math.floor(color.contrast(backgroundColor) * 100) / 100;
}
